import React, { useContext, useRef, useState, useEffect } from "react";
import { Field, Form, Formik, FormikProps } from "formik";
import Swal from "sweetalert2";
import FormWrapper from "components/FormWrapper";
import Modal from "react-modal";
import * as Yup from "yup";
import { AppContext } from "state";
import ResetAndSubmit from "components/ResetAndSubmit";
import { ChevronLeft, ChevronRight, Check, X } from "lucide-react";
import { motion } from "framer-motion";
import Instructions from "./Instructions";
import Upload from "./Upload";
import Consent from "./Consent";
import AIAvatarSubmission from "./AIAvatarSubmission";
import axios from "axios";

// Define the form values interface
interface FormValues {
  AVATAR_URL: string;
  AVATAR_INFO_ID?: number;
}

interface AvatarData {
  AVATAR_INFO_ID: number;
  NAME: string;
  THUMBNAIL: string;
}

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "80vw",
    minHeight: "90vh",
    borderRadius: "1.5em",
    boxShadow: "0px 16px 32px 4px rgba(0, 0, 0, 0.15)",
    padding: "1.3em",
    background: "#FFFFFF",
  },
  overlay: {
    background: "rgba(0, 0, 0, 0.4)",
    zIndex: 999,
  },
};

const chatbotModalTabs = [
  { name: "Instructions", value: "instructions" },
  { name: "Consent", value: "consent" },
  { name: "Upload", value: "upload" },
  { name: "Success", value: "success" },
];

const initialValues: FormValues = {
  AVATAR_URL: "",
};

const validationSchema = Yup.object<FormValues>({
  AVATAR_URL: Yup.string().required("Please select the Avatar"),
});

const ChatBot = ({
  handleNavigate,
  resetFormData,
  titleAndDesNotRequired,
  disableForm,
}: {
  handleNavigate;
  resetFormData?: any;
  titleAndDesNotRequired?: boolean;
  disableForm?: boolean;
}) => {
  const { globalData } = useContext(AppContext);
  const scrollContainerRef = useRef(null);

  const [showModal, setshowModal] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>("instructions");
  const [avatarModalOpen, setAvatarModalOpen] = useState<boolean>(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string>("");
  const [avatarInfoId, setAvatarInfoId] = useState<number>(0);

  const [avatars, setAvatars] = useState<AvatarData[]>([]);
  const [loadingAvatars, setLoadingAvatars] = useState(true);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const getModalStyles = () => {
    if (window.matchMedia("(max-width: 768px)").matches) {
      // Apply smaller styles for mobile and tablet
      return {
        ...customStyles,
        content: {
          ...customStyles.content,
          width: "90%", // Set to a percentage or auto
        },
      };
    }
    // Default styles for larger screens
    return customStyles;
  };

  const loadInitialData = (): FormValues => {
    try {
      return (
        JSON.parse(sessionStorage.getItem("Chatbot") || "{}") || initialValues
      );
    } catch (err) {
      return initialValues;
    }
  };

  const handleTabChange = (tabValue) => {
    setSelectedTab(tabValue);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -190,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 190,
        behavior: "smooth",
      });
    }
  };

  const onModalClose = () => {
    setshowModal(!showModal);
    setSelectedTab("instructions");
  };

  const handleConfirmAvatarCreation = () => {
    onModalClose();
    handleNavigate();
  };

  const scrollToSelectedAvatar = (avatarSrc: string) => {
    if (scrollContainerRef.current) {
      const selectedIndex = avatars.findIndex(
        (avatar) => avatar.THUMBNAIL === avatarSrc
      );
      if (selectedIndex !== -1) {
        const itemWidth = 188;
        const containerWidth = scrollContainerRef.current.clientWidth;
        const targetScrollLeft =
          selectedIndex * itemWidth - containerWidth / 2 + itemWidth / 2;

        scrollContainerRef.current.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth",
        });
      }
    }
  };

  const fetchAvatars = async () => {
    try {
      setLoadingAvatars(true);
      setAvatarError(null);

      const response = await axios.get(
        "https://dev.soomuch.ai/api/v1/master/avatar/list"
      );
      const result = response.data;

      if (result.code === 200 && result.data) {
        const validAvatars = result.data.filter(
          (avatar: any) => avatar.THUMBNAIL && avatar.THUMBNAIL.trim() !== ""
        );
        setAvatars(validAvatars);
      } else {
        setAvatarError("Failed to load avatars");
      }
    } catch (error) {
      console.error("Error fetching avatars:", error);
      setAvatarError("Error loading avatars. Please try again.");
    } finally {
      setLoadingAvatars(false);
    }
  };

  useEffect(() => {
    fetchAvatars();
  }, []);

  useEffect(() => {
    const initialData = loadInitialData();
    if (initialData.AVATAR_URL) {
      setSelectedAvatar(initialData.AVATAR_URL);
      setTimeout(() => {
        scrollToSelectedAvatar(initialData.AVATAR_URL);
      }, 100);
    }
  }, [avatars]);

  return (
    <Formik<FormValues>
      initialValues={loadInitialData()}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values: FormValues) => {
        const updatedValues = { ...values, AVATAR_INFO_ID: avatarInfoId };
        sessionStorage.setItem("Chatbot", JSON.stringify(updatedValues));
        sessionStorage.removeItem("CreateChatbotAvatar");
        handleNavigate();
      }}
    >
      {({
        handleSubmit,
        resetForm,
        errors,
        setFieldValue,
        values,
      }: FormikProps<FormValues>) => (
        <FormWrapper
          title={"Meet Your AI Assistant"}
          description={
            "Select an avatar and voice to personalize your chatbot experience."
          }
          childern={
            <Form onSubmit={handleSubmit} noValidate>
              {!!showModal && (
                <Modal
                  isOpen={showModal}
                  contentLabel="Example Modal"
                  style={getModalStyles()}
                >
                  <div className="space-y-4">
                    <div className="w-full flex justify-between items-center">
                      <div></div>
                      <div className="flex relative justify-between items-center gap-4">
                        {selectedTab !== "success" &&
                          chatbotModalTabs.slice(0, -1).map((tab) => (
                            <div
                              key={tab.value}
                              className={`w-full text-center px-2 py-3 text-[2.2vh] cursor-pointer relative ${
                                selectedTab === tab.value
                                  ? "text-indigo-600 border-b font-medium"
                                  : "text-gray-500 border-b border-[#fff]"
                              }`}
                              // onClick={() => handleTabChange(tab.value)}
                            >
                              {tab.name}
                              {selectedTab === tab.value && (
                                <motion.div
                                  layoutId="activeTab"
                                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ duration: 0.1 }}
                                />
                              )}
                            </div>
                          ))}
                      </div>
                      <div>
                        {selectedTab !== "success" && (
                          <X
                            className="w-4 h-4 cursor-pointer"
                            onClick={onModalClose}
                          />
                        )}
                      </div>
                    </div>
                    {selectedTab === "instructions" ? (
                      <Instructions
                        onNext={() => {
                          handleTabChange("upload");
                        }}
                      />
                    ) : selectedTab === "consent" ? (
                      <Consent />
                    ) : selectedTab === "upload" ? (
                      <Upload
                        onSubmit={() => {
                          handleTabChange("success");
                        }}
                      />
                    ) : selectedTab === "success" ? (
                      <AIAvatarSubmission
                        handleConfirmAvatarCreation={
                          handleConfirmAvatarCreation
                        }
                      />
                    ) : null}
                  </div>
                </Modal>
              )}
              {!!avatarModalOpen && (
                <Modal
                  isOpen={avatarModalOpen}
                  onRequestClose={() => setAvatarModalOpen(false)}
                  style={getModalStyles()}
                  contentLabel="Select Avatar"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-semibold">
                        Choose an Avatar
                      </h2>
                      <X
                        className="cursor-pointer"
                        onClick={() => setAvatarModalOpen(false)}
                      />
                    </div>

                    {loadingAvatars ? (
                      <div className="flex justify-center items-center w-full h-48">
                        <div className="text-[#222222] text-[2vh]">
                          Loading avatars...
                        </div>
                      </div>
                    ) : avatarError ? (
                      <div className="flex flex-col justify-center items-center w-full h-48 gap-2">
                        <div className="text-red-500 text-[2vh]">
                          {avatarError}
                        </div>
                        <button
                          type="button"
                          onClick={fetchAvatars}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                        >
                          Retry
                        </button>
                      </div>
                    ) : avatars.length === 0 ? (
                      <div className="flex justify-center items-center w-full h-48">
                        <div className="text-gray-500">
                          No avatars available
                        </div>
                      </div>
                    ) : (
                      <Field name="AVATAR_URL">
                        {({ field, form }) => (
                          <div className="flex flex-wrap gap-4">
                            {avatars.map((avatar) => (
                              <img
                                key={avatar.AVATAR_INFO_ID}
                                src={avatar.THUMBNAIL}
                                alt={avatar.NAME}
                                onClick={() => {
                                  form.setFieldValue(
                                    "AVATAR_URL",
                                    avatar.THUMBNAIL
                                  );
                                  setSelectedAvatar(avatar.THUMBNAIL);
                                  setAvatarInfoId(avatar?.AVATAR_INFO_ID);
                                  setAvatarModalOpen(false);
                                  setTimeout(() => {
                                    scrollToSelectedAvatar(avatar.THUMBNAIL);
                                  }, 300);
                                }}
                                className={`cursor-pointer border-4 rounded-md object-cover h-40 w-[24vh] transition-all duration-200 ${
                                  selectedAvatar === avatar.THUMBNAIL
                                    ? "border-indigo-600 scale-105"
                                    : "border-transparent"
                                }`}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </Field>
                    )}
                  </div>
                </Modal>
              )}
              <div className="bg-white flex gap-8 mx-1">
                {loadingAvatars ? (
                  <div className="flex-1 flex justify-center items-center h-40">
                    <div className="text-[#222222] text-[2.5vh] text-center">
                      Loading avatars...
                    </div>
                  </div>
                ) : avatars.length === 0 ? (
                  <div className="flex-1 flex justify-center items-center h-40">
                    <div className="text-[#222222] text-[2.5vh] text-center">
                      No avatars available
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col gap-2 px-1 overflow-x-auto">
                    {errors.AVATAR_URL && (
                      <div className="text-red-500 text-[2.5vh] text-center">
                        {errors.AVATAR_URL}
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-2 sm:flex-col sm:gap-2">
                      <div
                        ref={scrollContainerRef}
                        className="flex gap-3 items-center overflow-x-auto scrollbar-hide"
                        style={{
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        {avatars.map((avatar) => (
                          <img
                            key={avatar.AVATAR_INFO_ID}
                            src={avatar.THUMBNAIL}
                            alt={avatar.NAME}
                            onClick={() => {
                              setSelectedAvatar(avatar.THUMBNAIL);
                              setAvatarInfoId(avatar.AVATAR_INFO_ID);
                              setFieldValue("AVATAR_URL", avatar.THUMBNAIL);
                            }}
                            className={`w-44 h-48 object-cover border-4 flex-shrink-0 cursor-pointer transition-all duration-150 ${
                              selectedAvatar === avatar.THUMBNAIL
                                ? "border-indigo-600"
                                : "border-gray-200"
                            }`}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-10">
                      <button
                        type="button"
                        onClick={scrollLeft}
                        className="border border-gray-400 rounded-[50%] p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <p
                        className="text-center text-sm font-medium text-indigo-600 cursor-pointer"
                        onClick={() => setAvatarModalOpen(true)}
                      >
                        See All
                      </p>
                      <button
                        type="button"
                        onClick={scrollRight}
                        className="border border-gray-400 rounded-[50%] p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>

                    {/* <button
                    type="button"
                    onClick={() => {
                      if (!selectedAvatar) {
                        // Show validation error if no avatar selected
                        Swal.fire({
                          icon: "error",
                          title: "Avatar Required",
                          text: "Please select an avatar before proceeding.",
                        });
                        return;
                      }
                      setFieldValue("AVATAR_URL", selectedAvatar);
                      // You can add additional logic here if needed
                    }}
                    className="w-fit mx-auto py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-[#fff] rounded-lg text-sm font-medium"
                  >
                    Select
                  </button> */}
                  </div>
                )}

                <div className="flex flex-col items-center py-3 mr-3">
                  <div className="h-[90%] border border-dashed"></div>
                  <span className="text-[2vh]">OR</span>
                  <div className="h-[90%] border border-dashed"></div>
                </div>

                <div className="flex-1 border border-gray-100 rounded-xl py-5 px-5 bg-[#F5F5F5]">
                  <h3 className="text-[2.5vh] font-manropeSB mb-2 sm:text-lg text-center">
                    Create Your Own Realistic AI Avatar
                  </h3>
                  <p className="text-[2vh] text-gray-500 mb-2 text-center">
                    Upload a photo of the person you'd like to replicate (e.g.,
                    principal, or teacher). Our AI will create a lifelike avatar
                    for your school's chatbot.
                  </p>

                  <div className="flex items-center gap-6 bg-[rgba(27,168,86,0.12)] border border-green-100 rounded-lg p-[1.8vh] mb-3">
                    <div className="bg-[#1BA856] rounded-lg p-2">
                      <p className="text-[#fff]/[0.85] bg-[#F5F5F5]/[0.25] rounded-md px-2 py-1 font-semibold text-[1.6vh] mb-1 ">
                        One time purchase
                      </p>
                      <p className="text-[#fff] text-[3.4vh] font-bold mb-2 sm:text-xl">
                        ₹5,000
                      </p>
                      <p className="text-[#fff] text-[2vh]">Per Ai Avatar</p>
                    </div>
                    <ul className="text-[1.8vh] text-[#202020] space-y-2">
                      <li className="flex gap-2 items-center">
                        <Check className="w-3 h-3" />
                        <span>High-resolution AI-generated motion avatar</span>
                      </li>
                      <li className="flex gap-2 items-center">
                        <Check className="w-3 h-3" />
                        <span> Full commercial usage right </span>
                      </li>
                      <li className="flex gap-2 items-center">
                        <Check className="w-3 h-3" />
                        <span>2 free revisions if needed </span>
                      </li>
                      <li className="flex gap-2 items-center">
                        <Check className="w-3 h-3" />
                        <span>Support for all digital & print use </span>
                      </li>
                    </ul>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setSelectedAvatar("");
                      setAvatarInfoId(0);
                      // resetFormData();
                      sessionStorage.removeItem("Chatbot");

                      setshowModal(!showModal);
                    }}
                    className="block mx-auto py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-[#fff] rounded-lg text-sm font-medium"
                  >
                    Create My Own AI Avatar
                  </button>
                </div>
              </div>
              {!disableForm && (
                <ResetAndSubmit
                  resetForm={() => {
                    resetForm();
                    setSelectedAvatar("");
                    setAvatarInfoId(0);
                    resetFormData();
                    sessionStorage.removeItem("CreateChatbotAvatar");
                  }}
                />
              )}
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default ChatBot;
